import { FormGroup, FormSection } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IService } from '@common/interfaces/';
import { ManagerAuthorization } from '@common/enums';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization, useAbortableFetch } from '@frontend/utils';
import type { IReactSelectOptions } from '../../../../interfaces/reactSelectOptions';
import type { IServiceGraderPool } from '../../../serviceGraderPools/interfaces/serviceGraderPoolInterfaces';
import { DropdownAsyncLoadInput } from '../../../../fields/DropdownAsyncLoad';

export interface IServiceGraderPoolProps {
    error?: string;
    onChange: (serviceGraderPoolId: string | null) => void;
    onDoValidate?: () => void;
    service: IService;
    serviceGraderPoolId: string | null;
}

export function ServiceGraderPoolSetings(props: IServiceGraderPoolProps) {
    assertHasAuthorization([ManagerAuthorization.organizationAddEdit, ManagerAuthorization.employmentSettingsEdit]);

    const abortableFetch = useAbortableFetch();
    const [serviceGraderPools, setServiceGraderPools] = useState<IServiceGraderPool[]>([]);

    useEffect(() => {
        loadServiceGraderPools();
    }, []);

    if (serviceGraderPools.length === 0) {
        return null;
    }

    return (
        <FormSection title={translateKey('serviceGraderPool.serviceGraderPool')}>
            <FormGroup
                hasErrors={Boolean(props.error)}
                label={translateKey('serviceGraderPool.serviceGraderPool')}
            >
                <DropdownAsyncLoadInput<string>
                    error={props.error}
                    loadOptions={async (searchText) => await loadServiceGraderPoolOptions(searchText)}
                    onChange={async (value) => props.onChange(value)}
                    onDoValidate={async () => {
                        if (props.onDoValidate) {
                            props.onDoValidate();
                        }
                    }}
                    placeholder={translateKey('serviceGraderPool.selectServiceGraderPool')}
                    refreshToken={`${props.service.id}-${serviceGraderPools.length}`}
                    value={props.serviceGraderPoolId}
                />
            </FormGroup>
        </FormSection>
    );

    async function loadServiceGraderPoolOptions(searchText: string) {
        const regExp = new RegExp(searchText, 'i'); // search text case insensitive

        return serviceGraderPools
            .filter((graderPool) => !searchText || graderPool.name.search(regExp) !== -1)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((graderPool) => {
                return {
                    value: graderPool.id!,
                    label: graderPool.name,
                };
            }) as IReactSelectOptions[];
    }

    async function loadServiceGraderPools() {
        const url = `/api/serviceGraderPools?serviceId=${props.service.id}`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setServiceGraderPools(result.json.items);
    }
}
